import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

interface InverterModelOption {
  invType: string;
  device_count: number;
  no_of_strings: number | null;
}

interface StringConfigRow {
  id: number | null;
  invType: string;
  no_of_strings: number | null;
  device_count: number;
  updated_at: string | null;
}

/**
 * String Configuration: set no_of_strings once per inverter model (invType)
 * — it cascades to every device sharing that model. The one set/update
 * endpoint (POST /string-configurations/) handles create and update. The
 * listing below shows EVERY devType=1 model (configured or not), not just
 * the ones already configured, so every model can be reached from here.
 */
@Component({
  selector: 'app-string-configuration',
  templateUrl: './string-configuration.component.html',
  styleUrls: ['./string-configuration.component.scss'],
})
export class StringConfigurationComponent implements OnInit, OnDestroy {
  @Input() permissionsOverride?: { canAdd: boolean; canEdit: boolean; canView: boolean; canDelete: boolean };

  form: FormGroup;
  inverterModels: InverterModelOption[] = [];
  rows: StringConfigRow[] = [];

  loader = false;
  tableLoader = false;
  saving = false;
  /** Set while the modal edits an existing row (model select is locked). */
  editingInvType: string | null = null;

  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady = false;

  searchTerm = '';
  currentPage = 1;
  totalPages = 1;
  totalRecords = 0;
  nextPage: number = null;
  previousPage: number = null;
  pageToGo: number = null;
  private readonly pageSize = 10;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private permService: PermissionsService,
    private modalService: NgbModal
  ) {
    this.form = this.fb.group({
      invType: [null, Validators.required],
      no_of_strings: [null, [Validators.required, Validators.min(0)]],
    });
  }

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((term: string) => {
      this.searchTerm = term;
      this.currentPage = 1;
      this.loadRows();
    });
    this.initComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async initComponent(): Promise<void> {
    this.loader = true;
    try {
      await this.getComponentPermissions();
      this.loadInverterModels();
      this.loadRows();
    } finally {
      this.loader = false;
    }
  }

  async getComponentPermissions(): Promise<void> {
    if (this.permissionsOverride) {
      this.permissions = { ...this.permissionsOverride };
      this.permissionsReady = true;
      return;
    }

    const url = this.permService.getCurrentUrl();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
          canAdd: !!matchedMenu.canAdd,
          canEdit: !!matchedMenu.canEdit,
          canView: !!matchedMenu.canView,
          canDelete: !!matchedMenu.canDelete,
        }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }

  loadInverterModels(): void {
    this._utilityService.getInverterModelsDropdown().subscribe({
      next: (res: InverterModelOption[]) => {
        this.inverterModels = res || [];
      },
      error: () => this.toaster.error('Failed to load inverter models'),
    });
  }

  loadRows(): void {
    this.tableLoader = true;
    this._utilityService
      .getStringConfigurations({ search: this.searchTerm, page: this.currentPage, page_size: this.pageSize })
      .subscribe({
        next: (res: any) => {
          this.rows = res?.results || [];
          const pagination = res?.pagination || {};
          this.totalRecords = pagination.total_records || 0;
          this.totalPages = pagination.total_pages || 1;
          this.currentPage = pagination.current_page || 1;
          this.nextPage = pagination.next_page ?? null;
          this.previousPage = pagination.previous_page ?? null;
          this.tableLoader = false;
        },
        error: () => {
          this.rows = [];
          this.tableLoader = false;
          this.toaster.error('Failed to load string configurations');
        },
      });
  }

  searchData(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value || '';
    this.searchSubject.next(value);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadRows();
    }
  }

  navigatePage(direction: 'prev' | 'next'): void {
    if (direction === 'prev' && this.previousPage !== null) {
      this.goToPage(this.previousPage);
    } else if (direction === 'next' && this.nextPage !== null) {
      this.goToPage(this.nextPage);
    }
  }

  onModelChange(invType: string): void {
    const match = this.inverterModels.find((m) => m.invType === invType);
    this.form.patchValue({ no_of_strings: match?.no_of_strings ?? null });
  }

  openConfigModal(modal: any, row?: StringConfigRow): void {
    this.editingInvType = row?.invType ?? null;
    this.form.reset({ invType: row?.invType ?? null, no_of_strings: row?.no_of_strings ?? null });
    const invTypeCtrl = this.form.get('invType');
    row ? invTypeCtrl.disable() : invTypeCtrl.enable();
    this.modalService.open(modal, { centered: true });
  }

  save(closeModal: (reason?: any) => void): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    // getRawValue: invType is disabled (locked) when editing an existing row.
    this._utilityService.setStringConfiguration(this.form.getRawValue()).subscribe({
      next: () => {
        this.saving = false;
        this.toaster.success('String configuration saved');
        closeModal();
        this.loadInverterModels();
        this.loadRows();
      },
      error: (error: HttpErrorResponse) => {
        this.saving = false;
        this.handleApiFieldErrors(error);
      },
    });
  }

  private handleApiFieldErrors(error: HttpErrorResponse): void {
    const errors = error.error?.errors;
    if (Array.isArray(errors)) {
      errors.forEach((blocker: any) => {
        this.toaster.error(`${blocker.devName} (${blocker.pv}): ${blocker.panel_count} panel(s) linked`);
      });
      this.toaster.error(error.error?.message || 'Cannot reduce no_of_strings while panels are linked.');
      return;
    }
    if (error.error?.message) {
      this.toaster.error(error.error.message);
      return;
    }
    this.toaster.error('Sorry, something went wrong. Please try again.');
  }
}
